"use client";

import { useRef } from "react";
import { Container, SectionTitle, BodyText } from "./Primitives";
import type { CompanyAchievements } from "@/lib/companySettings";

/**
 * Achievements — заголовок + параграф + горизонтальная карусель картинок.
 *
 * Все данные строго из админ-настроек (`/company/achievements`). Если
 * блок не настроен (нет ни title, ни body, ни картинок) — секция не
 * рендерится.
 */
export default function Achievements({
  achievements,
}: {
  achievements?: CompanyAchievements;
} = {}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const block = achievements ?? {};
  const images = block.images ?? [];
  const empty = !block.title && !block.body && images.length === 0;

  function scrollBy(direction: "prev" | "next") {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-card]");
    if (!card) return;
    const gap = 20;
    const delta = (card.offsetWidth + gap) * (direction === "next" ? 1 : -1);
    el.scrollBy({ left: delta, behavior: "smooth" });
  }

  if (empty) return null;

  return (
    <section id="achievements" className="pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-[85px] lg:pb-[85px]">
      <Container>
        {/* Lead */}
        {(block.title || block.body) && (
          <div className="mb-10 grid gap-6 sm:mb-12 lg:grid-cols-[minmax(260px,400px)_1fr] lg:gap-[120px] lg:mb-[55px]">
            {block.title && <SectionTitle>{block.title}</SectionTitle>}
            <div className="flex flex-col gap-6">
              {block.body && <BodyText>{block.body}</BodyText>}
              {images.length > 0 && (
                <div className="hidden justify-end gap-3 sm:flex">
                  <CarouselButton onClick={() => scrollBy("prev")} label="Предыдущая награда" direction="prev" />
                  <CarouselButton onClick={() => scrollBy("next")} label="Следующая награда" direction="next" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Carousel */}
        {images.length > 0 && (
          <div className="relative">
            <div
              ref={scrollerRef}
              className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {images.map((src, i) => (
                <div
                  key={i}
                  data-card
                  className="shrink-0 snap-start w-[130px] sm:w-[160px] lg:w-[calc((100%-100px)/6)]"
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded-[16px]">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={src}
                      alt={`Награда ${i + 1}`}
                      className="absolute inset-0 size-full object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Container>
    </section>
  );
}

function CarouselButton({
  onClick,
  label,
  direction,
}: {
  onClick: () => void;
  label: string;
  direction: "prev" | "next";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex size-11 items-center justify-center rounded-full border border-black/15 bg-white text-black transition-all hover:border-black hover:-translate-y-0.5 hover:shadow-md"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={direction === "prev" ? "rotate-180" : ""}
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </button>
  );
}
